import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RootTheme } from '@yandex-360/orb-components/dist/components/Theme';
import cn from 'classnames';
import { Helmet } from 'react-helmet';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../init-store';
import { i18n } from '../../../helpers/i18n';

import '../../../helpers/fonts/fonts-cloud.css';

import { useLocation } from 'react-router-dom';

import { REGEX_SHORT_URL_ID } from '../../../../../../common/helpers';
import TelemostImage from '../../../../images/og-telemost.png';
import {
    getConferenceStatus,
    getJoinedAsGuest,
    isChatAllowedInConference,
    isSidebarVisible,
} from '../../../../pages/call/selectors';
import { TeleMessengerPlaceholderPage } from '../../../../pages/telemessenger-placeholder';
import CoverLayer from '../../../components/cover-layer';
import { ErrorPageType } from '../../../components/error-page/types';
import { ThemeBackground } from '../../../components/theme-background';
import { ICON_SOURCES } from '../../../helpers/favicons/constants';
import { getThemeClassName, isDarkTheme } from '../../../helpers/theme';
import {
    getBodyThemeName,
    getConfig,
    isCoverLayerEnabled,
    isMessengerIntegration,
    selectHideNonCallsRelatedInterface,
    selectIsB2BHost,
    selectIsGlobalBarEnabled,
    selectIsMeetingRoomIntegration,
    selectIsTeleMessenger,
    selectIsTelemostDesktopIntegration,
    selectTeleMessengerRedirectUrl,
} from '../../config/selectors';
import { selectIsRoomAgentShellEnabled, selectIsWarmupModeEnabled } from '../../flags/selectors';
import { PromozavrRuntime } from '../../promozavr';
import { SettingsVersioner } from '../../settings-modal';
import { isMobileUa } from '../../uatraits';
import { isPDDUserSelector, isYandexSsoUserSelector } from '../../user/selectors';
import styles from '../index.css';
import { CallsWithSidebarView, DefaultView, OnlyCallsView } from './components';

const mapState = (state: RootState) => {
    const joinedAsGuest = getJoinedAsGuest(state);
    const conferenceStatus = getConferenceStatus(state);
    const { errorPageType, appLang, autoJoin, isOnPremise, telemostHost } = getConfig(state);
    return {
        errorPageType,
        appLang,
        isChatAllowed: isChatAllowedInConference(state),
        isSidebarVisible: isSidebarVisible(state),
        joinedAsGuest,
        conferenceStatus,
        newTouchStub: errorPageType === ErrorPageType.UNSUPPORTED_TOUCH,
        autoJoin,
        isMessengerIntegration: isMessengerIntegration(state),
        isCoverLayerEnabled: isCoverLayerEnabled(state),
        isYandexSsoUser: isYandexSsoUserSelector(state),
        isB2BHost: selectIsB2BHost(state),
        isOnPremise,
        telemostHost,
        isTeleMessenger: selectIsTeleMessenger(state),
    };
};

const connector = connect(mapState);

const TELEMESSENGER_PATHS = ['/chats', '/user', '/p/', '/c/', '/staff', '/join', '/link'] as const;

type RootContainerProps = ConnectedProps<typeof connector>;

const RootContainer: FunctionComponent<RootContainerProps> = ({
    errorPageType,
    appLang,
    joinedAsGuest,
    autoJoin,
    conferenceStatus,
    newTouchStub,
    isChatAllowed,
    isSidebarVisible,
    isMessengerIntegration,
    isCoverLayerEnabled,
    isYandexSsoUser,
    isB2BHost,
    isOnPremise,
    telemostHost,
    isTeleMessenger,
}) => {
    const hideNonCallsRelatedInterface = useSelector(selectHideNonCallsRelatedInterface);
    const isMeetingRoomIntegration = useSelector(selectIsMeetingRoomIntegration);
    const isTelemostDesktopIntegration = useSelector(selectIsTelemostDesktopIntegration);
    const isGlobalBarEnabled = useSelector(selectIsGlobalBarEnabled);
    const teleMessengerRedirectUrl = useSelector(selectTeleMessengerRedirectUrl);
    const { pathname } = useLocation();
    const isConferencesHistory = pathname.startsWith('/conferences-history');
    const isMainPage = pathname === '/';
    const isLive = pathname.startsWith('/live/');

    const isMobile = useSelector(isMobileUa);
    const [hideHeader, setHideHeader] = useState(
        isMessengerIntegration || (isMobile && (isConferencesHistory || isLive)) || isTeleMessenger
    );
    const onOverlayHide = useCallback(() => setHideHeader(true), []);

    const [withChat, setWithChat] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null);

    const isJoin = REGEX_SHORT_URL_ID.test(pathname);
    const isTeleMessengerPath = TELEMESSENGER_PATHS.some((path) => pathname.startsWith(path));

    const { title, description, ogTitle, ogDescription } = useMemo(() => {
        if (isTeleMessengerPath) {
            let ogMeta = {};
            if (pathname.startsWith('/join')) {
                ogMeta = {
                    ogTitle: i18n('header', 'telemessenger-chat-og-title'),
                    ogDescription: i18n('header', 'app-telemessenger-chat-og-description'),
                };
            }
            if (pathname.startsWith('/link')) {
                ogMeta = {
                    ogTitle: i18n('header', 'telemessenger-invite-og-title'),
                    ogDescription: i18n('header', 'app-telemessenger-invite-og-description'),
                };
            }
            return {
                title: i18n('header', 'telemessenger-title'),
                description: i18n('header', 'app-telemessenger-description'),
                ...ogMeta,
            };
        }
        if (isJoin) {
            return {
                title: i18n('header', 'join-title'),
                description: i18n('header', 'app-join-description'),
                ogTitle: i18n('header', 'join-og-title'),
                ogDescription: i18n('header', 'app-join-og-description'),
            };
        }
        if (isConferencesHistory) {
            return {
                title: i18n('conferences-history', 'conferences-history'),
            };
        }
        return {
            title: i18n('header', 'title'),
            description: i18n('header', 'app-description'),
        };
    }, [isTeleMessengerPath, isJoin, isConferencesHistory, pathname]);
    useEffect(() => {
        const hidden =
            (isJoin && (!joinedAsGuest || Boolean(autoJoin))) ||
            (isConferencesHistory && (isMobile || isTelemostDesktopIntegration)) ||
            isMessengerIntegration ||
            isMeetingRoomIntegration ||
            (isMobile && isLive) ||
            isTeleMessenger;
        setHideHeader(hidden);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pathname,
        joinedAsGuest,
        autoJoin,
        isConferencesHistory,
        isMobile,
        isMessengerIntegration,
        isMeetingRoomIntegration,
        isTeleMessenger,
    ]);

    useEffect(() => {
        setWithChat(isChatAllowed);
    }, [conferenceStatus, isChatAllowed, joinedAsGuest]);

    const shouldRenderThemeBackground =
        !isJoin &&
        !isLive &&
        !hideNonCallsRelatedInterface &&
        !isMessengerIntegration &&
        !isMeetingRoomIntegration &&
        !isTeleMessenger;
    const shouldRenderCover = isCoverLayerEnabled && shouldRenderThemeBackground;
    const bodyThemeName = useSelector((state) => getBodyThemeName(state, pathname));
    const isDark = isDarkTheme(bodyThemeName);
    const withoutRightPadding = pathname.startsWith('/conferences-history');

    const isPdd = useSelector(isPDDUserSelector);
    const shouldShowNativeAppBanner =
        !isMessengerIntegration &&
        !isMeetingRoomIntegration &&
        !isConferencesHistory &&
        !isOnPremise &&
        (!isMainPage || isPdd) &&
        !isTeleMessenger;

    const isWarmupModeEnabled = useSelector(selectIsWarmupModeEnabled);
    const isRoomAgentShellEnabled = useSelector(selectIsRoomAgentShellEnabled);
    const ogUrl = `https://${telemostHost}`;

    return (
        <RootTheme
            theme={isDark ? 'dark' : 'light'}
            brand="telemost"
            lightThemeClassName={getThemeClassName('light')}
            darkThemeClassName={getThemeClassName('dark')}
        >
            {!isRoomAgentShellEnabled && <PromozavrRuntime />}
            <div
                ref={parentRef}
                className={cn([styles.root, 'ps-header-exp'], {
                    [styles.rootTouch]: newTouchStub,
                    [styles.withChat]: withChat,
                    [styles.rowLayout]: isSidebarVisible,
                    [styles.globalBarLayout]: isGlobalBarEnabled && !hideHeader,
                    [styles.centerContent]: !isLive && !hideHeader,
                    [styles.withoutRightPadding]: withoutRightPadding,
                })}
            >
                {shouldRenderCover ? <CoverLayer /> : null}
                {shouldRenderThemeBackground ? <ThemeBackground /> : null}
                <Helmet>
                    {/* Класс font_loaded нужен для библиотеки yandex-font */}
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <html lang={appLang} className="font_loaded" prefix="og: http://ogp.me/ns#" />
                    <title>{title}</title>
                    <meta name="description" content={description} />
                    <meta property="og:title" content={ogTitle || title} />
                    <meta property="og:url" content={ogUrl} />
                    <meta property="og:description" content={ogDescription || description} />
                    <meta property="og:image" content={TelemostImage} />
                    <meta property="og:type" content="website" />
                    {!isOnPremise && <meta name="yandex-verification" content="bdd5e4f331529c13" />}
                    {!isOnPremise && (
                        <meta name="google-site-verification" content="hl9kVXPLesMZW5Ts0W-fNvhUCbEp6sGJdq2EZtP1x9o" />
                    )}
                    <meta name="apple-itunes-app" />
                    {(isB2BHost || isOnPremise) && <meta name="robots" content="noindex, nofollow" />}
                    {isConferencesHistory && <meta name="format-detection" content="telephone=no" />}
                    {ICON_SOURCES.map(({ size, url, rel }) => {
                        return (
                            <link
                                key={`icon-${size}`}
                                rel={rel}
                                type="image/png"
                                sizes={`${size}x${size}`}
                                href={url}
                            />
                        );
                    })}
                </Helmet>

                {isWarmupModeEnabled ? (
                    <div>Warmup page: OK</div>
                ) : teleMessengerRedirectUrl ? (
                    <TeleMessengerPlaceholderPage redirectUrl={teleMessengerRedirectUrl} />
                ) : (
                    <>
                        {hideNonCallsRelatedInterface ? (
                            <OnlyCallsView errorPageType={errorPageType} />
                        ) : isMeetingRoomIntegration ? (
                            <CallsWithSidebarView withChat={withChat} errorPageType={errorPageType} />
                        ) : (
                            <DefaultView
                                hiddenHeader={hideHeader}
                                withChat={withChat}
                                errorPageType={errorPageType}
                                onCallOverlayHide={onOverlayHide}
                                isPlanningDisabled={isYandexSsoUser}
                                shouldShowNativeAppBanner={shouldShowNativeAppBanner}
                            />
                        )}

                        {!isRoomAgentShellEnabled && <SettingsVersioner parentRef={parentRef} />}
                    </>
                )}
            </div>
        </RootTheme>
    );
};

export default connector(RootContainer);
